// Actual domain logic should be in a domainLogic.js file, at the appropriate
// level in the hierarchy.
import { Decimal } from "classes/DecimalClasses";
import { Loading } from "classes/Loading";

import { updateLineItemSerials } from "services/sosInventoryService/domainLogic";
import { removeLotAndSerialValues } from "services/sosInventoryService/domainLogic";
import { updateProcessInputs } from "services/sosInventoryService/productionTransaction/domainLogic";
import { getRecord } from "services/sosInventoryService/sosApi";
import { setBinForInboundLines } from "services/utility/bins";
import { handleProgramError } from "services/utility/errors";

import globalState from "globalState/globalState";
import { linesLoadingOn, linesLoadingOff } from "globalState/loadingSlice";

export async function updateTransaction(
  field,
  newValue,
  currentState,
  updaters
) {
  const { record, outputLines, inputLines, relatedRecords } = currentState;
  if (!record || !outputLines || !inputLines || !relatedRecords) {
    handleProgramError(new Error("update Transaction | invalid currentState"));
  }

  let newRecord, newInputs, newRelatedRecords;

  switch (field) {
    case "date":
      newRecord = { ...record, date: newValue };
      updaters.setRecord(newRecord);
      if (newValue) {
        globalState.dispatch(linesLoadingOn());
        // show onhand skeleton while async data resolves
        updaters.inputLineHandler({
          type: "setFields",
          fields: { onhand: new Loading() },
        });
        try {
          newInputs = await updateProcessInputs(
            newValue,
            record.location,
            inputLines
          );
          // patch only computed fields; preserves any user edits made while async work was in flight
          updaters.inputLineHandler({
            type: "updateFields",
            lines: newInputs,
            fields: ["relatedRecords", "onhand", "uom", "availableBins", "bin"],
          });
        } catch (e) {
          handleProgramError(e);
        } finally {
          globalState.dispatch(linesLoadingOff());
        }
      }

      break;

    case "location":
      globalState.dispatch(linesLoadingOn());
      // show onhand skeleton while async data resolves
      updaters.inputLineHandler({
        type: "setFields",
        fields: { onhand: new Loading() },
      });
      try {
        newRecord = { ...record, location: newValue };
        updaters.setRecord(newRecord);
        const location = newValue
          ? await getRecord("location", newValue.id)
          : null;
        newRelatedRecords = { ...relatedRecords, location };
        const newOutputs = await setBinForInboundLines(outputLines, location);
        updaters.outputLineHandler({
          type: "updateFields",
          lines: newOutputs,
          fields: ["bin"],
        });
        newInputs = await updateProcessInputs(
          record.date,
          newValue,
          inputLines
        );
        newInputs = removeLotAndSerialValues(newInputs);
        // patch only computed fields; preserves any user edits made while async work was in flight
        updaters.inputLineHandler({
          type: "updateFields",
          lines: newInputs,
          fields: [
            "relatedRecords",
            "onhand",
            "uom",
            "availableBins",
            "bin",
            "lot",
            "serials",
          ],
        });
      } catch (e) {
        handleProgramError(e);
      } finally {
        globalState.dispatch(linesLoadingOff());
      }

      break;

    case "comment":
    case "customFields":
    case "department":
    case "number":
    case "startDate":
    case "template":
    case "workcenter":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);

      break;
    case "autoSerialLotNumbering":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);
      const newOutputs = updateLineItemSerials(newValue, outputLines);
      updaters.outputLineHandler({ type: "set", lines: newOutputs });
      break;

    case "multiplier":
      newRecord = { ...record, [field]: new Decimal(newValue || 0) };
      updaters.setRecord(newRecord);
      break;

    default:
      handleProgramError(
        new Error(`updateTransaction | unknown field (${field})`)
      );
  }

  if (newRelatedRecords) {
    updaters.setRelatedRecords(newRelatedRecords);
  }
}
